// #region IMPORTS
import React from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { Tooltip } from '../Tooltip/Tooltip';
import { isCurrentFeatureSeries } from '../../Utils/appVersion';
// CSS
import './NewFeatureBadge.css';
// #endregion

// #region COMPONENT
/**
 * Marks a feature as new for the length of a feature series. It shows while the
 * running release shares the major.minor of the version the feature shipped in,
 * so patch releases keep it and the next minor drops it. No state, no storage,
 * nothing to clean up.
 *
 * Reuse it by declaring the release a feature ships in:
 *   <NewFeatureBadge newInVersion="4.4.0" />
 */
const NewFeatureBadge = ({ newInVersion, label, variant }) => {
  // #region INITIALIZATION
  const [t] = useTranslation('Common');
  // #endregion

  // #region INTERFACE
  // Hidden once the running release left the feature series
  if (!isCurrentFeatureSeries(newInVersion)) {
    return null;
  }

  const badge = (
    <span className={`newFeatureBadge newFeatureBadge--${variant}`}>
      {label || t('New')}
    </span>
  );

  if (variant !== 'corner') {
    return badge;
  }

  // The corner glyph is explained by a tooltip
  return (
    <>
      {badge}
      <Tooltip>{t('New')}</Tooltip>
    </>
  );
  // #endregion
};
// #endregion

// #region PROPERTIES
NewFeatureBadge.propTypes = {
  /** The release this feature shipped in, ie. '4.4.0' */
  newInVersion: PropTypes.string.isRequired,
  /** Overrides the translated default, which the caller must translate itself */
  label: PropTypes.string,
  /**
   * `inline` is a labelled pill sitting in the flow. `corner` is a one glyph
   * disc pinned to the top right of the closest positioned ancestor, for which
   * the caller is expected to pass a short `label`.
   */
  variant: PropTypes.oneOf(['inline', 'corner']),
};

NewFeatureBadge.defaultProps = {
  variant: 'inline',
};
// #endregion

export { NewFeatureBadge };
